import { useQuery } from '@apollo/client';
import {
  ActionGroup,
  PermissionToggle,
  Loader,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  getTranslatedText,
  getParsedCrudData,
  ALL_SHARD_MODULES
} from '@nile/components';
import {
  Heading,
  Page,
  TextStyle,
  ResourceList,
  ResourceItem,
  InlineEdit,
  DisplayText
} from '@superops/ui-design-system';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { AccountClient, GET_FIELD } from '@nile/graphql';
import { SVGIcon } from '@superops/super-icons';
import { Tooltip } from 'react-clone-tippy';
import { isEmpty, map, orderBy, pick } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';

const SVG_ICON_MAP = {
  0: {
    color: '#D72C0D',
    iconName: 'UpArrow'
  },
  1: {
    color: '#FF7F23',
    iconName: 'UpArrow'
  },
  2: {
    color: '#00D247',
    iconName: 'DownArrow'
  }
};

const TooltipMap = {
  0: 'admin.passwordPolicy.type.high',
  1: 'admin.passwordPolicy.type.medium',
  2: 'admin.passwordPolicy.type.low'
};

const UrgencyWrapper = styled.div`
  .heading {
    font-weight: 600;
    font-size: 20px;
    line-height: 21px;
    margin-bottom: 10px;
  }
  .description {
    font-size: 18px;
    line-height: 27px;
    margin-bottom: 20px;
  }

  .urgency-wrapper {
    max-width: 400px;
  }
`;

const UrgencyItem = styled.div`
  &.isEdit {
    padding: 6px 20px 12px;
  }
  &:hover {
    .pencil {
      visibility: visible;
    }
  }
`;

const UrgencyItemComponent = (props) => {
  const { index, intl } = props;
  const [edit, setEdit] = useState(false);
  const { item } = props;
  const id = item?.id;
  const value = item?.value;

  const handleFocusOut = (updatedText) => {
    props.handleFocusOut(item, updatedText);
    setEdit(false);
  };

  const { color, iconName } = SVG_ICON_MAP?.[index] || {};
  const tooltip = TooltipMap?.[index];
  const tooltipContent = `${getTranslatedText(
    intl,
    'admin.ImpactUrgencyPriority.Urgency'
  )}: ${getTranslatedText(intl, tooltip)}`;

  if (edit) {
    return (
      <UrgencyItem className="fx actr isEdit g5" superid={`UrgencyEdit-${id}`}>
        <div className="flex g5 actr">
          {iconName && (
            <div className="flex">
              <SVGIcon name={iconName} color={color} />
            </div>
          )}
          <div className="fx actr urgency-list">
            <Heading element="h3">
              <InlineEdit
                text={value}
                labelClassName="InlineEditLabel"
                inputClassName="InlineEditInput"
                inputWidth="180px"
                inputHeight="25px"
                onFocus={() => {}}
                isEditing
                onFocusOut={handleFocusOut}
              />
            </Heading>
          </div>
        </div>
      </UrgencyItem>
    );
  }

  return (
    <ResourceItem
      id={`urgency${id}`}
      accessibilityLabel={`View details for ${value}`}
    >
      <UrgencyItem className="fx actr hiddenContainer">
        <div className="flex g5 actr">
          {iconName && (
            <Tooltip
              title={tooltipContent || ''}
              delay={200}
              duration={250}
              position="bottom"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <div className="flex pointer">
                <SVGIcon name={iconName} color={color} />
              </div>
            </Tooltip>
          )}
          <div className="fx actr urgency-list">
            <DisplayText element="p" size="large" weight="Medium">
              <div className="flex actr">
                <div className="mr8">{value}</div>
                {props.isEditAuthorised && (
                  <div
                    className="hiddenFxActions"
                    onClick={(e) => {
                      setEdit(true);
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <SVGIcon name="Edit" />
                  </div>
                )}
              </div>
            </DisplayText>
          </div>
        </div>
      </UrgencyItem>
    </ResourceItem>
  );
};

function Urgency(props) {
  const { isEditAuthorised } = props;
  const [mandatoryOnCreation, setMandatoryOnCreation] = useState(false);
  const [mandatoryOnClosure, setMandatoryOnClosure] = useState(false);
  const [urgencyData, setUrgencyData] = useState([]);
  const flags = useFlags();
  const intl = useIntl();

  const { data, loading, error } = useQuery(GET_FIELD, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        entity: ALL_SHARD_MODULES.ticket,
        columnName: DEFAULT_FIELDS_BY_COLUMN_NAME.URGENCY
      }
    },
    onCompleted({ getField }) {
      if (getField?.options || getField?.fieldOptions) {
        setUrgencyData(
          orderBy(getField?.options, 'order') || getField?.fieldOptions
        );
      }
      setMandatoryOnCreation(getField?.mandatoryOnCreate);
      setMandatoryOnClosure(getField?.mandatoryOnClosure);
    }
  });

  const updateUrgency = (item, text) => {
    const base = [...urgencyData];
    let updatedUrgencies = [];
    base.forEach((el) => {
      if (el?.id === item?.id) {
        const updatedData = {
          ...el,
          value: text
        };
        updatedUrgencies = [...updatedUrgencies, updatedData];
      } else {
        updatedUrgencies = [...updatedUrgencies, el];
      }
    });
    setUrgencyData(updatedUrgencies);
  };

  function handleFocusOut(item, text) {
    const trimmedValue = text.trim();
    if (!trimmedValue) return;
    updateUrgency(item, trimmedValue);
  }

  if (error) return <div>error occured </div>;

  const primaryAction = () => {
    const columnName = DEFAULT_FIELDS_BY_COLUMN_NAME.URGENCY;
    const baseData = data?.getField?.options || data?.getField?.fieldOptions;
    const updatedData = urgencyData;
    const result = getParsedCrudData(baseData, updatedData);
    let updatedOptions = {};
    const { updatedItems } = result;
    if (updatedItems.length > 0) {
      updatedOptions = {
        ...updatedOptions,
        updated: updatedItems,
        updateOptions: updatedItems
      };
    }

    let variables = {
      id: data?.getField?.id,
      mandatoryOnCreate: mandatoryOnCreation,
      mandatoryOnClosure: mandatoryOnClosure
    };

    if (flags?.dependentField) {
      variables = {
        ...variables,
        updateOptions: map(updatedOptions?.updateOptions, (e) =>
          pick(e, ['id', 'value', 'order'])
        )
      };
    } else {
      variables = {
        ...variables,
        fieldOptionsInput: isEmpty(updatedOptions)
          ? undefined
          : JSON.stringify(updatedOptions)
      };
    }
    props.primaryAction(variables);
  };

  return (
    <UrgencyWrapper className="full-height full-width">
      <div className="full-height full-width">
        <Page
          fullWidth
          variant="primary"
          contentHeight="140px"
          title={
            <Heading element="h1">
              <FormattedMessage id="admin.mandatoryFields.edit.default.Fields" />
            </Heading>
          }
          primaryAction={
            <ActionGroup
              primaryText={<FormattedMessage id="common.save" />}
              primaryLoading={props.saveLoading}
              primaryAction={primaryAction}
              secondaryAction={props.secondaryAction}
              primaryDisabled={!isEditAuthorised}
            />
          }
        >
          <TextStyle variation="strong" weight="Bold">
            <p className="heading">
              <FormattedMessage id="admin.ImpactUrgencyPriority.Urgency" />
            </p>
          </TextStyle>
          <TextStyle variation="subdued">
            <p className="description">
              <FormattedMessage id="admin.ImpactUrgencyPriority.Urgency.desc" />
            </p>
          </TextStyle>

          {loading ? (
            <>
              <div className="no-scroll">
                <Loader />
              </div>
            </>
          ) : (
            <>
              <div className="mb10 urgency-wrapper">
                {urgencyData.map((item, index) => {
                  return (
                    <div key={item.id} className="flex g5 mb10">
                      <div>
                        <UrgencyItemComponent
                          item={item}
                          handleFocusOut={handleFocusOut}
                          isEditAuthorised={isEditAuthorised}
                          index={index}
                          intl={intl}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mb40">
                <PermissionToggle
                  mandatoryOnCreation={mandatoryOnCreation}
                  setMandatoryOnCreation={setMandatoryOnCreation}
                  mandatoryOnClosure={mandatoryOnClosure}
                  setMandatoryOnClosure={setMandatoryOnClosure}
                  disableEdit={!isEditAuthorised}
                  showWarning={props.showWarning}
                  hideShowToClients
                />
              </div>
            </>
          )}
        </Page>
      </div>
    </UrgencyWrapper>
  );
}

export default Urgency;
